import {inject} from "@angular/core";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {Store} from "@ngrx/store";
import {MatSnackBar} from "@angular/material/snack-bar";
import {catchError, concatMap, exhaustMap, filter, interval, map, mergeMap, of, pairwise, startWith, switchMap, takeUntil, tap} from "rxjs";
import {AiSetupJobInfo} from "../../fund/generated-sources/api";
import {FundAiSetupActions} from "./fund-ai-setup.actions";
import {FundAiSetupService} from "../services/fund-ai-setup.service";
import {selectHasActiveJobsForFund} from "./fund-ai-setup.selectors";
import {ErrorHandlingUtil} from "../../shared/utils/error-handling-util";
import {NotificationActions} from "../../notification/store/notification.actions";

export const startAiSetup = createEffect(
    (actions$ = inject(Actions), service = inject(FundAiSetupService), snack = inject(MatSnackBar)) =>
        actions$.pipe(
            ofType(FundAiSetupActions.start),
            exhaustMap(({fundId, request}) =>
                service.start(fundId, request).pipe(
                    tap(() => snack.open("AI setup running in background — we'll notify you when it's ready", "OK", {duration: 4000})),
                    map(taskInfoId => FundAiSetupActions.startSuccess({fundId, taskInfoId})),
                    catchError(err => of(FundAiSetupActions.startError({
                        fundId,
                        errorMsg: ErrorHandlingUtil.getErrorMessage(err)
                    })))
                )
            )
        ),
    {functional: true}
);

// After a successful start, immediately reload jobs for that fund so the chip flips to "running"
export const reloadJobsAfterStart = createEffect(
    (actions$ = inject(Actions)) =>
        actions$.pipe(
            ofType(FundAiSetupActions.startSuccess),
            map(({fundId}) => FundAiSetupActions.loadJobs({fundId}))
        ),
    {functional: true}
);

// Switch the notification bell's poll cadence to 5 s while the AI Setup job is in flight.
// The matching JOB_FINISHED notification untracks itself when it arrives in the panel list.
export const trackAiSetupJob = createEffect(
    (actions$ = inject(Actions)) =>
        actions$.pipe(
            ofType(FundAiSetupActions.startSuccess),
            map(({taskInfoId}) => NotificationActions.trackJob({taskInfoId}))
        ),
    {functional: true}
);

export const loadJobs = createEffect(
    (actions$ = inject(Actions), service = inject(FundAiSetupService)) =>
        actions$.pipe(
            ofType(FundAiSetupActions.loadJobs),
            switchMap(({fundId}) =>
                service.getJobsForFund(fundId).pipe(
                    map(jobs => FundAiSetupActions.loadJobsSuccess({fundId, jobs})),
                    catchError(err => of(FundAiSetupActions.loadJobsError({
                        fundId,
                        errorMsg: ErrorHandlingUtil.getErrorMessage(err)
                    })))
                )
            )
        ),
    {functional: true}
);

// Poll every 5s while there's an active (NEW / IN_PROGRESS) job for the fund.
// Tears itself down when no active jobs remain for the same fund.
//
// Filters by fundId on both the outer trigger and the inner takeUntil so multiple
// concurrent fund pages don't cancel each other's polling intervals.
export const pollJobsWhileActive = createEffect(
    (actions$ = inject(Actions), store = inject(Store)) =>
        actions$.pipe(
            ofType(FundAiSetupActions.loadJobsSuccess),
            switchMap(({fundId}) =>
                store.select(selectHasActiveJobsForFund(fundId)).pipe(
                    switchMap(hasActive => {
                        if (!hasActive) return of();
                        return interval(5000).pipe(
                            map(() => FundAiSetupActions.loadJobs({fundId})),
                            takeUntil(actions$.pipe(
                                ofType(FundAiSetupActions.loadJobsSuccess),
                                filter(a => a.fundId === fundId)
                            ))
                        );
                    })
                )
            )
        ),
    {functional: true}
);

export const loadDetail = createEffect(
    (actions$ = inject(Actions), service = inject(FundAiSetupService)) =>
        actions$.pipe(
            ofType(FundAiSetupActions.loadDetail),
            switchMap(({taskInfoId}) =>
                service.getJobDetail(taskInfoId).pipe(
                    map(detail => FundAiSetupActions.loadDetailSuccess({detail})),
                    catchError(err => of(FundAiSetupActions.loadDetailError({
                        errorMsg: ErrorHandlingUtil.getErrorMessage(err)
                    })))
                )
            )
        ),
    {functional: true}
);

export const acceptResult = createEffect(
    (actions$ = inject(Actions), service = inject(FundAiSetupService)) =>
        actions$.pipe(
            ofType(FundAiSetupActions.acceptResult),
            // concatMap (not mergeMap) so accepts on the same asset run sequentially.
            // Each accept reads the asset, mutates one field, and saves — running them
            // concurrently causes a last-write-wins race on the asset row.
            concatMap(({resultId, request, taskInfoId}) =>
                service.acceptResult(resultId, request).pipe(
                    map(() => FundAiSetupActions.acceptResultSuccess({resultId, taskInfoId})),
                    catchError(err => of(FundAiSetupActions.acceptResultError({
                        resultId,
                        errorMsg: ErrorHandlingUtil.getErrorMessage(err)
                    })))
                )
            )
        ),
    {functional: true}
);

export const skipResult = createEffect(
    (actions$ = inject(Actions), service = inject(FundAiSetupService)) =>
        actions$.pipe(
            ofType(FundAiSetupActions.skipResult),
            concatMap(({resultId, taskInfoId}) =>
                service.skipResult(resultId).pipe(
                    map(() => FundAiSetupActions.skipResultSuccess({resultId, taskInfoId})),
                    catchError(err => of(FundAiSetupActions.skipResultError({
                        resultId,
                        errorMsg: ErrorHandlingUtil.getErrorMessage(err)
                    })))
                )
            )
        ),
    {functional: true}
);

// When a job transitions from NEW/IN_PROGRESS to COMPLETED and has zero generated results
// (e.g. FILL_BLANKS_ONLY where every selected section already had a value, or all sections
// hit the no-info sentinel), surface a snackbar so the user knows nothing's wrong — the
// run finished, there's just nothing to review. Without this the chip silently disappears.
const ACTIVE = new Set(["NEW", "IN_PROGRESS"]);
export const notifyNothingToReview = createEffect(
    (actions$ = inject(Actions), snack = inject(MatSnackBar)) =>
        actions$.pipe(
            ofType(FundAiSetupActions.loadJobsSuccess),
            map(action => action.jobs),
            startWith([] as AiSetupJobInfo[]),
            pairwise(),
            mergeMap(([prev, curr]) => {
                const wasActive = new Set<number>(
                    prev.filter(j => ACTIVE.has(j.status as unknown as string)).map(j => j.taskInfoId)
                );
                // Job completed since the previous tick AND has nothing for the user to look at
                return curr.filter(j =>
                    wasActive.has(j.taskInfoId)
                    && (j.status as unknown as string) === "COMPLETED"
                    && (j.totalResultCount ?? 0) === 0
                );
            }),
            tap(() => snack.open(
                "AI Setup finished — nothing to review (selected sections were already filled, or no info found).",
                "OK", {duration: 5000}
            ))
        ),
    {functional: true, dispatch: false}
);

// After accept/skip success, reload the detail so the result row updates
export const reloadDetailAfterAction = createEffect(
    (actions$ = inject(Actions)) =>
        actions$.pipe(
            ofType(FundAiSetupActions.acceptResultSuccess, FundAiSetupActions.skipResultSuccess),
            map(({taskInfoId}) => FundAiSetupActions.loadDetail({taskInfoId}))
        ),
    {functional: true}
);
